import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import {
  SEARCH_FAILED,
  searchFailed,
  searchStarted,
  searchSucceeded,
  selectSearchError,
  selectSearchLoading,
  selectSearchQuery,
  setQuery,
} from '@features/search/searchSlice';
import SearchBar from '@components/search/SearchBar';
import SearchFilters, {
  DEFAULT_SEARCH_FILTERS,
  SearchFiltersType,
} from '@components/search/SearchFilters';
import SearchResults from '@components/search/SearchResults';
import { useRememberedSearchParams } from '@hooks/useRememberedSearchParams';
import { formatIsoDate, parseIsoDate } from '@hooks/useDocumentsQuery';
import { useSearchFacets } from '@hooks/useSearchFacets';
import { SEARCH } from '@constants/search';
import { searchService } from '@services/search.service';
import type { SearchResult } from '../types';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const query = useAppSelector(selectSearchQuery);
  const isLoading = useAppSelector(selectSearchLoading);
  // The slice stores the wire code, never server prose; it becomes copy here.
  const errorCode = useAppSelector(selectSearchError);
  const error = errorCode
    ? resolveApiErrorMessage(t, { code: errorCode }, { fallbackKey: 'search.searchFailed' })
    : null;
  // Retrying cannot cure a missing session or permission (HTTP_401/403 and
  // the named AUTH_* codes).
  const canRetry = !/^(HTTP_40[13]|AUTH_)/.test(errorCode ?? '');

  const [params, setParams] = useRememberedSearchParams('search.filters');
  const filterParams = params.toString();
  const filters = useMemo<SearchFiltersType>(() => {
    const params = new URLSearchParams(filterParams);
    return {
      ...DEFAULT_SEARCH_FILTERS,
      categories: params.getAll('category'),
      tags: params.getAll('tag'),
      fileTypes: params.getAll('type'),
      uploadedBy: params.getAll('by'),
      dateRange: {
        start: parseIsoDate(params.get('from') ?? ''),
        end: parseIsoDate(params.get('to') ?? ''),
      },
      searchType: ['basic', 'semantic', 'exact'].includes(params.get('mode') ?? '')
        ? params.get('mode') as SearchFiltersType['searchType'] : 'basic',
      sortBy: ['relevance', 'date', 'title', 'size'].includes(params.get('sort') ?? '')
        ? params.get('sort') as SearchFiltersType['sortBy'] : 'relevance',
      sortOrder: params.get('dir') === 'asc' ? 'asc' : 'desc',
    };
  }, [filterParams]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(SEARCH.DEFAULT_PAGE_SIZE);
  const [searchTime, setSearchTime] = useState<number>();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const facets = useSearchFacets(results);
  // A slow response must not overwrite a newer search or end its loading state.
  const requestSeq = useRef(0);
  // The slice state is global while this counter is per mount, so unmount
  // retires every request this instance issued: none may end a search the
  // next mount started.
  useEffect(() => () => { requestSeq.current = -1; }, []);

  const runSearch = useCallback(
    async (searchQuery: string, searchFilters: SearchFiltersType, searchPage: number, size: number) => {
      if (!searchQuery.trim()) return;
      const startedAt = performance.now();
      const seq = ++requestSeq.current;
      dispatch(searchStarted());

      try {
        const response = await searchService.search({
          query: searchQuery,
          searchType: searchFilters.searchType,
          filters: {
            categories: searchFilters.categories,
            tags: searchFilters.tags,
            fileTypes: searchFilters.fileTypes,
            dateRange:
              searchFilters.dateRange.start || searchFilters.dateRange.end
                ? {
                    start: formatIsoDate(searchFilters.dateRange.start) || undefined,
                    end: formatIsoDate(searchFilters.dateRange.end) || undefined,
                  }
                : undefined,
            uploadedBy: searchFilters.uploadedBy,
          },
          page: searchPage,
          pageSize: size,
          sortBy: searchFilters.sortBy,
          sortOrder: searchFilters.sortOrder,
          includeFacets: true,
        });
        if (seq !== requestSeq.current) return;

        setResults(response.results);
        setTotalCount(response.total);
        setSearchTime((performance.now() - startedAt) / 1000);
        dispatch(searchSucceeded());

        // Analytics must never turn a delivered result into a failure.
        void searchService.trackSearch({
          query: searchQuery,
          searchType: searchFilters.searchType,
          resultsCount: response.total,
          filters: {
            categories: searchFilters.categories,
            tags: searchFilters.tags,
            fileTypes: searchFilters.fileTypes,
            uploadedBy: searchFilters.uploadedBy,
          },
        }).catch((trackErr: unknown) => {
          console.warn('Search tracking failed:', (trackErr as { code?: unknown } | null)?.code);
        });
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setResults([]);
        setTotalCount(0);
        // The server's prose belongs in neither the console nor the state, so a
        // wire failure logs its code only; our own bug (an Error) keeps its stack.
        const raw = (err as { code?: unknown } | null)?.code;
        const code = typeof raw === 'string' && raw ? raw : undefined;
        console.error('Search failed:', code ?? SEARCH_FAILED, ...(err instanceof Error ? [err] : []));
        // Without this the page shows its empty state and a failure is
        // indistinguishable from a search with no matches.
        dispatch(searchFailed(code));
      }
    },
    [dispatch],
  );

  // Page 3 of the previous query is meaningless against the next one, so a
  // query change sends the reader back to page 1. Render-time adjustment
  // rather than an effect: the wrong page number never reaches the network.
  const [prevQuery, setPrevQuery] = useState(query);
  if (query !== prevQuery) {
    setPrevQuery(query);
    if (page !== 1) setPage(1);
  }

  useEffect(() => {
    if (!query) return;
    // Debounced against per-keystroke updates from the search bar, and
    // stale-guarded so a slow response cannot overwrite a newer query's
    // results; the analytics POST only fires for the settled query.
    let stale = false;
    const timer = setTimeout(() => {
      if (!stale) runSearch(query, filters, page, pageSize);
    }, SEARCH.QUERY_DEBOUNCE_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [query, filters, page, pageSize, runSearch]);

  const applyFilters = (next: SearchFiltersType) => {
    setParams((current) => {
      const updated = new URLSearchParams(current);
      for (const [key, values] of Object.entries({
        category: next.categories, tag: next.tags, type: next.fileTypes, by: next.uploadedBy,
        from: [formatIsoDate(next.dateRange.start)], to: [formatIsoDate(next.dateRange.end)],
        mode: [next.searchType], sort: [next.sortBy], dir: [next.sortOrder],
      })) {
        updated.delete(key);
        values.forEach((value) => { if (value) updated.append(key, value); });
      }
      return updated;
    });
    setPage(1);
  };

  const filterPanel = (
    <SearchFilters onFiltersChange={applyFilters} filters={filters} facets={facets} />
  );

  return (
    <div className="@container/search flex flex-col gap-5">
      <div className="flex items-start justify-between gap-3">
        <h1 className="revy-cjk text-2xl font-semibold leading-[1.4] tracking-tight">
          {t('search.searchDocuments')}
        </h1>

        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="shrink-0 @3xl/search:hidden">
              <SlidersHorizontal className="size-4" strokeWidth={1.9} />
              {t('search.filters')}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="revy-ui w-[min(20rem,100vw)] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{t('search.filters')}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <SearchFilters
                onFiltersChange={applyFilters}
                filters={filters}
                facets={facets}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <SearchBar
        onSearch={(searchQuery) => {
          dispatch(setQuery(searchQuery));
          setPage(1);
        }}
        placeholder={t('search.searchPlaceholder')}
      />

      <div className="grid items-start gap-5 @3xl/search:grid-cols-[18rem_1fr]">
        <div className="hidden @3xl/search:block">{filterPanel}</div>

        <div className="min-w-0">
          <SearchResults
            results={results}
            isLoading={isLoading}
            error={error}
            onRetry={canRetry ? () => runSearch(query, filters, page, pageSize) : undefined}
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onResultClick={(result) => navigate(`/documents/${result.id}`)}
            searchTime={searchTime}
            query={query}
          />
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
